// Masquerade : la plateforme se déconstruit, passe par un vortex, et se reconstruit sous
// l'autre identité. Réservé aux admins (masquerade l'est déjà), donc on peut se permettre
// une mise en scène.
//
// Deux actes, de part et d'autre d'une navigation :
//
//   Acte I  — implosion. Au clic, la page VISIBLE est décomposée en morceaux de squelette,
//             puis aspirés en spirale vers un vortex central. La navigation ne part qu'après
//             DEPART_DELAY_MS : le document sortant reste peint par le navigateur jusqu'au
//             premier paint du suivant, donc l'animation couvre toute la latence d'un seul
//             tenant, sans relais ni second moule.
//
//   Acte II — explosion. La page d'arrivée est rendue MASQUÉE (classe posée par le serveur,
//             cf. layouts/_body.html.erb : sans elle on verrait un éclair de contenu avant
//             de pouvoir le cacher en JS). On mesure ses blocs, on les recrée au centre du
//             vortex, on les projette à leur place, puis le vrai contenu prend le relais en
//             fondu.
//
// Ce qui rend l'effet lisible, c'est que les morceaux NE SONT PAS des rectangles génériques :
// chaque élément est traduit dans le vocabulaire de squelette de la plateforme — barre de
// texte à 35/55/80 % comme `__cell-bar`, pilule de bouton, champ de recherche arrondi, carré
// d'icône, pastille d'avatar (dans sa vraie couleur), bloc de graphe — et peint avec la même
// matière que les vrais squelettes, la classe `.is-loading`. On reconnaît sa propre page.
//
// Les morceaux sont des PROXYS : le vrai DOM n'est jamais déplacé, seulement masqué sous
// l'overlay. Rien à remettre en place, aucune interaction avec le morphing Turbo, et
// l'overlay est retiré avant tout snapshot (cf. turbo:before-cache plus bas).
//
// Tout est en `transform` / `opacity` : le compositeur suffit, même à 120 morceaux. Le
// miroitement de `.is-loading`, lui, repeint à chaque frame — il est donc coupé pendant le
// vol (cf. FLYING_CLASS) et rallumé seulement quand les morceaux sont posés.

const OVERLAY_CLASS = 'masquerade-vortex';
const FLYING_CLASS = 'masquerade-vortex--flying';
const PENDING_CLASS = 'masquerade-vortex-pending';
const TRIGGER_SELECTOR = 'a[data-masquerade-vortex]';

// Le clic laisse ce délai à l'implosion avant de lancer la navigation. En dessous, une
// réponse rapide coupe le gag en deux ; au-dessus, on ajoute de la latence pour rien.
const DEPART_DELAY_MS = 220;
const SKELETONIZE_MS = 140;
const IMPLODE_MS = 560;
const IMPLODE_STAGGER_MS = 340;
const BURST_DELAY_MS = 160;
const BURST_MS = 620;
const BURST_STAGGER_MS = 380;
const REVEAL_MS = 320;
// Temps de miroitement une fois tout posé, avant le fondu vers le vrai contenu : c'est ce
// battement qui fait lire « squelette de la plateforme » plutôt que « tas de rectangles ».
const SHIMMER_BEFORE_REVEAL_MS = 140;
const TOAST_MS = 3800;
// Filet : si l'acte II ne démarre pas (JS en erreur, page sans overlay), le contenu ne doit
// jamais rester masqué. Doublé côté CSS par une animation de secours sur .masquerade-vortex-pending.
const FAILSAFE_MS = 4000;

const MAX_SHARDS = 120;
// En dessous, on ne joue PAS la déconstruction : seul le noyau apparaît. Une page qui n'a
// presque rien à décomposer — un index vide, une page dont les frames paresseuses n'ont pas
// encore répondu — donnait une poignée de barres qui traversaient un écran blanc, et ça se
// lisait comme un raté plutôt que comme un effet. Mieux vaut assumer le noyau seul.
const MIN_SHARDS = 20;
// Un élément ATOMIQUE — donc sans rien où descendre — qui couvre presque tout l'écran n'est
// pas un morceau, c'est un calque. Cas réel rencontré : le `<canvas>` plein écran que
// js-confetti laisse sur `<body>` après sa première utilisation (`position: fixed`,
// `pointer-events: none`, invisible, z-index 1000). ATOMIC_SELECTOR le prenait tel quel,
// sans borne de taille, d'où la grande plaque pivotée au milieu du vortex — et seulement sur
// les pages où les confettis avaient déjà tourné, d'où le « pas à tous les coups ».
const ATOMIC_MAX_AREA_RATIO = 0.55;
const MIN_SHARD_WIDTH = 10;
const MIN_SHARD_HEIGHT = 8;
// Simple garde-fou de récursion, PAS un réglage esthétique : le vrai plafond est `hardCap`,
// puis `MAX_SHARDS`. À 16, la descente s'arrêtait avant d'atteindre les cellules de tableau et
// les pilules de filtre — mesuré sur l'index Contrats, dont le DOM descend à 26 niveaux
// (app-shell > container > row > col > turbo-frame > table > tbody > tr > td > …) : 27 morceaux
// au lieu d'une centaine, et une implosion qui se lisait comme un raté. Le coût de la descente
// tient au NOMBRE d'éléments (843 sur cette page, 16 ms), pas à la profondeur.
const MAX_WALK_DEPTH = 40;
// Au-delà, un bloc est découpé plutôt que pris tel quel : une ligne de tableau pleine
// largeur n'est pas un morceau, c'est une poutre qui balaie l'écran en tournant.
const MAX_ATOMIC_WIDTH = 420;

const REM = 16;

const SKIP_SELECTOR = [
  'script', 'style', 'template', 'noscript', 'link', 'meta', 'br', 'hr',
  `.${OVERLAY_CLASS}`, '.masquerade-vortex-toast', '.nav-instant-skeleton',
  '#flashes-container', '.modal-backdrop', '.offcanvas-backdrop',
  '.tooltip', '.popover', '.dropdown-menu',
].join(',');

// --- vocabulaire de squelette -----------------------------------------------------------

type ShardKind = 'text' | 'icon' | 'pill' | 'field' | 'action' | 'avatar' | 'block';

// `in` : on entre dans la peau de quelqu'un. `out` : on regagne la sienne. `space` : on
// bascule d'espace sans changer de compte. `sign_in` / `sign_out` : on entre dans la
// plateforme ou on en sort — la même scène à l'endroit et à l'envers, déclenchée depuis un
// formulaire et non un lien (cf. auth_vortex.ts).
type VortexDirection =
  | 'in'
  | 'out'
  | 'space'
  | 'arcade'
  | 'arcade_out'
  | 'sign_in'
  | 'sign_out';
const DIRECTIONS: readonly string[] = [
  'in',
  'out',
  'space',
  'arcade',
  'arcade_out',
  'sign_in',
  'sign_out',
];

const AVATAR_SELECTOR = '.avatar, .avatar-lg, .avatar-xl';
const ICON_SELECTOR = [
  '.sidebar-nav-link__icon', '.sidebar-nav-link__caret',
  '.sidebar-context-bandeau__switch-icon',
  'i[class*="fa-"]', 'svg.svg-inline--fa',
].join(',');
const FIELD_SELECTOR = [
  '.form-control', '.global-search-bar', '.index-search-bar',
  'input', 'textarea', 'select',
].join(',');
const CONTROL_SELECTOR = '.btn, button, .badge, .depozen-ui-filter-pill';
const MEDIA_SELECTOR = 'canvas, video, img';

// Ne jamais découper : ces éléments SONT un morceau. Testé avant tout le reste, pour qu'un
// `.avatar` (un span qui contient une image) ne parte pas en miettes.
const ATOMIC_SELECTOR = [AVATAR_SELECTOR, ICON_SELECTOR, FIELD_SELECTOR, MEDIA_SELECTOR].join(',');

// Toujours découper : ce sont des assemblages reconnaissables (icône + libellé, avatar +
// nom + rôle), et c'est justement ce détail qui fait qu'on reconnaît la sidebar. Sans ça,
// un lien de nav devenait une barre pleine largeur et la colonne de gauche ressemblait à
// une pile de briques.
const SPLIT_SELECTOR = [
  '.sidebar-nav-link',
  '.sidebar-context-bandeau__context-card',
  '.sidebar-my-account',
  '.card',
  '.index-preview-row',
].join(',');

// Une cellule qui n'abrite qu'un jeton visuel autonome — pastille d'avatar, badge de statut,
// pilule — n'est pas une feuille : c'est le jeton qu'on veut, pas la cellule qui l'enveloppe.
// Sans ça la colonne d'avatars d'un index devenait une colonne de barres grises, et les
// statuts perdaient leur forme de pilule. Limité aux cellules et aux éléments de liste :
// descendre dans un bouton ferait perdre sa pilule au profit de son icône.
const TOKEN_HOST_SELECTOR = 'td, th, li';
const TOKEN_CHILD_SELECTOR = `${AVATAR_SELECTOR}, .badge, .depozen-ui-filter-pill`;

// Assemblages qu'on ne découpe pas plus finement : ils se lisent déjà comme « un bloc ».
const LEAF_SELECTOR = [
  'a', 'label', 'td', 'th', 'li', 'p',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  CONTROL_SELECTOR,
].join(',');

// Ce qui est vert à l'écran, et rien d'autre : le bouton principal de la sidebar, l'onglet
// actif, une pilule de filtre active. Le reste de la plateforme est gris — c'est ce qui la
// rend reconnaissable. `closest` et pas `matches` : le libellé d'un bouton primaire doit
// prendre la couleur de son bouton.
const ACCENT_SELECTOR = '.btn-primary, .text-primary-emphasis, .depozen-ui-filter-pill--active';

// Mêmes proportions que `.turbo-index-loading-skeleton__cell-bar` (short / medium / long) :
// des barres toutes identiques se lisent comme un gabarit, pas comme du contenu.
const TEXT_WIDTHS = [0.8, 0.55, 0.35];

interface VortexAvatar {
  // Mode fun : au centre du vortex, c'est la borne qu'on veut voir, pas un portrait.
  glyph: string | null;
  url: string | null;
  initials: string;
  background: string;
  // `circle` pour un portrait ou un favicon, `wordmark` pour un logo texte — large, il a sa
  // propre plaque.
  shape: 'circle' | 'wordmark';
  // `contain` pour un logo d'espace, `cover` pour un portrait.
  fit: 'cover' | 'contain';
  // Traitement de marque, décidé par le serveur avec les helpers de la plateforme.
  // `null` pour une photo de profil, qui n'en relève pas.
  theme: 'invert' | 'solid' | null;
}

interface VortexColors {
  light: string | null;
  dark: string | null;
}

interface VortexPayload {
  // Identifiant unique de CETTE scène, généré par le serveur. Voir `alreadyPlayed`.
  id: string;
  direction: VortexDirection;
  name: string;
  icon: string;
  avatar: VortexAvatar;
  color: VortexColors;
  caption: string;
  done: string;
}

interface ShardBox {
  left: number;
  top: number;
  width: number;
  height: number;
  radius: string;
  kind: ShardKind;
  accent: boolean;
  // Couleur réelle d'une pastille d'avatar : la palette de la plateforme, telle quelle.
  color: string | null;
}

const prefersReducedMotion = () =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Laisser passer une frame avant de mesurer, SANS en dépendre : dans un onglet en arrière-plan
// requestAnimationFrame peut ne jamais se déclencher, et la page d'arrivée resterait masquée
// jusqu'au filet de sécurité. On fait donc la course avec un minuteur, premier arrivé gagne.
const afterNextFrame = (run: () => void): void => {
  let done = false;
  const once = () => {
    if (done) return;
    done = true;
    run();
  };
  requestAnimationFrame(once);
  window.setTimeout(once, 60);
};

const parseAvatar = (raw: unknown): VortexAvatar => {
  const value = (raw ?? {}) as Record<string, unknown>;
  return {
    glyph: typeof value.glyph === 'string' && /^[a-z0-9 -]{1,64}$/.test(value.glyph) ? value.glyph : null,
    url: typeof value.url === 'string' ? value.url : null,
    initials: String(value.initials ?? '?'),
    background: typeof value.background === 'string' ? value.background : '',
    shape: value.shape === 'wordmark' ? 'wordmark' : 'circle',
    fit: value.fit === 'contain' ? 'contain' : 'cover',
    theme: value.theme === 'invert' || value.theme === 'solid' ? value.theme : null,
  };
};

const parseColors = (raw: unknown): VortexColors => {
  const value = (raw ?? {}) as Record<string, unknown>;
  return {
    light: safeColor(value.light),
    dark: safeColor(value.dark),
  };
};

// Les couleurs viennent de `companies.color`, une valeur saisie en base. On n'accepte qu'un
// hexadécimal : elle finit dans une propriété CSS, pas question d'y laisser passer autre
// chose.
const safeColor = (raw: unknown): string | null =>
  typeof raw === 'string' && /^#[0-9a-f]{3,8}$/i.test(raw.trim()) ? raw.trim() : null;

// L'icône du toast vient d'une constante serveur, mais elle est posée en classe : on s'en
// tient au vocabulaire Font Awesome.
const safeIconClass = (raw: unknown): string =>
  typeof raw === 'string' && /^[a-z0-9 -]{1,64}$/.test(raw) ? raw : 'fa-solid fa-user-secret';

const parsePayload = (raw: string | null | undefined): VortexPayload | null => {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!DIRECTIONS.includes(parsed?.direction)) return null;
    return {
      id: String(parsed.id ?? ''),
      direction: parsed.direction,
      name: String(parsed.name ?? ''),
      icon: safeIconClass(parsed.icon),
      avatar: parseAvatar(parsed.avatar),
      color: parseColors(parsed.color),
      caption: String(parsed.caption ?? ''),
      done: String(parsed.done ?? ''),
    };
  } catch {
    return null;
  }
};

// ---------------------------------------------------------------------------------------
// Récolte : on descend dans le DOM tant qu'un élément est trop gros ou trop composite pour
// être un morceau, et on s'arrête dès qu'il se lit comme une brique de squelette.
// ---------------------------------------------------------------------------------------

function kindFor(el: Element, rect: DOMRect): ShardKind {
  if (el.matches(AVATAR_SELECTOR)) return 'avatar';
  if (el.matches(ICON_SELECTOR)) return 'icon';
  if (el.matches(FIELD_SELECTOR)) return 'field';
  if (el.matches(MEDIA_SELECTOR)) return 'block';
  // Un bouton à peu près carré est un bouton d'icône (export, dossier, notifications) :
  // il se rend en carré, pas en pilule étirée.
  if (el.matches(CONTROL_SELECTOR)) return rect.width <= rect.height * 1.5 ? 'action' : 'pill';
  if (rect.width <= 26 && rect.height <= 26) return 'icon';
  if (rect.height > 64) return 'block';
  return 'text';
}

interface Geometry {
  left: number;
  top: number;
  width: number;
  height: number;
  radius: string;
}

const centeredIn = (rect: DOMRect, size: number, radius: string): Geometry => ({
  left: rect.left + (rect.width - size) / 2,
  top: rect.top + (rect.height - size) / 2,
  width: size,
  height: size,
  radius,
});

const bandIn = (rect: DOMRect, height: number, radius: string): Geometry => ({
  left: rect.left,
  top: rect.top + (rect.height - height) / 2,
  width: rect.width,
  height,
  radius,
});

function geometryFor(
  rect: DOMRect,
  kind: ShardKind,
  textAlign: string,
  index: number
): Geometry {
  switch (kind) {
    case 'avatar':
      return centeredIn(rect, Math.min(rect.width, rect.height), '50%');
    case 'icon':
      return centeredIn(rect, Math.min(rect.width, rect.height, 1.125 * REM), '0.3125rem');
    case 'action':
      return centeredIn(rect, Math.min(rect.width, rect.height, 1.875 * REM), '0.375rem');
    case 'pill':
      return bandIn(rect, Math.min(rect.height, 1.75 * REM), '50rem');
    case 'field':
      return bandIn(rect, Math.min(rect.height, 2 * REM), '50rem');
    case 'block':
      return { left: rect.left, top: rect.top, width: rect.width, height: rect.height, radius: '0.75rem' };
    default: {
      // Barre de texte : hauteur et largeurs de `__cell-bar`, alignée comme l'est le texte
      // qu'elle remplace — sinon une colonne de montants alignée à droite se retrouve
      // barrée à gauche.
      const height = Math.min(0.875 * REM, rect.height);
      const width = Math.max(
        Math.min(rect.width * TEXT_WIDTHS[index % TEXT_WIDTHS.length], rect.width),
        Math.min(20, rect.width)
      );
      let left = rect.left;
      if (textAlign === 'right' || textAlign === 'end') left = rect.right - width;
      else if (textAlign === 'center') left = rect.left + (rect.width - width) / 2;

      return { left, top: rect.top + (rect.height - height) / 2, width, height, radius: '0.25rem' };
    }
  }
}

const opaqueColor = (value: string): string | null => {
  if (!value) return null;
  if (value === 'transparent' || value.startsWith('rgba(0, 0, 0, 0')) return null;
  return value;
};

// La récolte, plus le seuil de dégradation. Une seule passe : la descente coûte ~16 ms sur un
// index chargé, il n'est pas question de la faire deux fois.
function harvestOrGiveUp(): ShardBox[] {
  const boxes = collectShardBoxes();
  return boxes.length >= MIN_SHARDS ? boxes : [];
}

function collectShardBoxes(): ShardBox[] {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const viewportArea = vw * vh;
  const boxes: ShardBox[] = [];
  // Garde-fou de parcours : au-delà, on a de toute façon largement de quoi remplir l'écran.
  const hardCap = MAX_SHARDS * 6;

  const walk = (parent: Element, depth: number): void => {
    for (const el of Array.from(parent.children)) {
      if (boxes.length >= hardCap) return;
      if (el.matches(SKIP_SELECTOR)) continue;

      const style = getComputedStyle(el);
      if (style.display === 'none' || style.visibility === 'hidden') continue;
      if (Number(style.opacity) === 0) continue;

      const rect = el.getBoundingClientRect();
      if (rect.bottom <= 0 || rect.top >= vh || rect.right <= 0 || rect.left >= vw) continue;
      if (rect.width < MIN_SHARD_WIDTH || rect.height < MIN_SHARD_HEIGHT) continue;

      const hostsToken =
        el.matches(TOKEN_HOST_SELECTOR) && el.querySelector(TOKEN_CHILD_SELECTOR) !== null;

      const atomic =
        el.matches(ATOMIC_SELECTOR) ||
        (!el.matches(SPLIT_SELECTOR) &&
          !hostsToken &&
          (el.children.length === 0 ||
            el.matches(LEAF_SELECTOR) ||
            (rect.height <= 56 &&
              rect.width <= MAX_ATOMIC_WIDTH &&
              rect.width * rect.height < viewportArea * 0.22)));

      if (!atomic && el.children.length > 0 && depth < MAX_WALK_DEPTH) {
        walk(el, depth + 1);
        continue;
      }

      if (atomic && rect.width * rect.height > viewportArea * ATOMIC_MAX_AREA_RATIO) continue;

      const kind = kindFor(el, rect);
      const geometry = geometryFor(rect, kind, style.textAlign, boxes.length);
      if (geometry.width < 4 || geometry.height < 3) continue;

      boxes.push({
        // Bornés au viewport : un morceau qui dépasse ferait apparaître une barre de scroll
        // au moment où l'overlay se pose.
        left: Math.max(geometry.left, 0),
        top: Math.max(geometry.top, 0),
        width: Math.min(geometry.width, vw - Math.max(geometry.left, 0)),
        height: Math.min(geometry.height, vh - Math.max(geometry.top, 0)),
        radius: geometry.radius,
        kind,
        accent: Boolean(el.closest(ACCENT_SELECTOR)),
        color: kind === 'avatar' ? opaqueColor(style.backgroundColor) : null,
      });
    }
  };

  walk(document.body, 0);

  if (boxes.length <= MAX_SHARDS) return boxes;

  // Échantillonnage régulier plutôt que troncature : l'ordre du DOM suit grosso modo
  // l'ordre spatial, donc un pas constant garde des morceaux répartis sur tout l'écran —
  // là où un `slice` ne garderait que le haut de la page.
  const step = boxes.length / MAX_SHARDS;
  const sampled: ShardBox[] = [];
  for (let i = 0; i < MAX_SHARDS; i += 1) sampled.push(boxes[Math.floor(i * step)]);
  return sampled;
}

// ---------------------------------------------------------------------------------------
// Construction de l'overlay
// ---------------------------------------------------------------------------------------

const div = (className: string): HTMLElement => {
  const node = document.createElement('div');
  node.className = className;
  return node;
};

function buildShard(box: ShardBox): HTMLElement {
  // `is-loading` : la matière exacte des squelettes de la plateforme (dégradé + miroitement,
  // valeurs nuit comprises). Rien n'est réécrit ici, on emprunte.
  const shard = div(`masquerade-vortex__shard masquerade-vortex__shard--${box.kind} is-loading`);
  if (box.accent) shard.classList.add('masquerade-vortex__shard--accent');
  shard.style.left = `${box.left}px`;
  shard.style.top = `${box.top}px`;
  shard.style.width = `${box.width}px`;
  shard.style.height = `${box.height}px`;
  shard.style.borderRadius = box.radius;
  // Une pastille d'avatar garde sa vraie couleur : c'est la seule tache de couleur d'un
  // index, et c'est elle qui fait dire « c'est bien ma liste ».
  if (box.color) shard.style.background = box.color;
  return shard;
}

// L'URL vient du serveur (`rails_blob_path`, chemin absolu). On refuse tout ce qui pourrait
// pointer ailleurs — `//hôte`, `javascript:`, une URL absolue.
function safeAvatarUrl(url: string | null): string | null {
  if (!url || !url.startsWith('/') || url.startsWith('//')) return null;
  return url;
}

const SVG_NS = 'http://www.w3.org/2000/svg';

// Anneaux tracés au `stroke` plutôt qu'en dégradé conique masqué. Le masque radial exigeait
// une bande de transition entre le plein et le vide, et c'est elle qui rendait les cercles
// flous — un arc tracé reste net à toute taille.
//
// Deux arcs concentriques par anneau plutôt qu'un seul : une traîne longue et discrète, puis
// une tête courte et franche posée à son extrémité. C'est ce qui rend l'effet de comète que
// donnait le dégradé conique, sans le flou qui allait avec.
//
// `pathLength="100"` normalise la circonférence : longueurs et décalages s'expriment alors
// directement en pourcentage de tour.
function buildRing(modifier: string, radius: number, trail: number, head: number): SVGSVGElement {
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('viewBox', '0 0 100 100');
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('class', `masquerade-vortex__ring masquerade-vortex__ring--${modifier}`);

  const arc = (className: string, length: number, offset: number): SVGCircleElement => {
    const circle = document.createElementNS(SVG_NS, 'circle');
    circle.setAttribute('class', className);
    circle.setAttribute('cx', '50');
    circle.setAttribute('cy', '50');
    circle.setAttribute('r', String(radius));
    circle.setAttribute('pathLength', '100');
    circle.setAttribute('stroke-dasharray', `${length} ${100 - length}`);
    if (offset) circle.setAttribute('stroke-dashoffset', String(-offset));
    return circle;
  };

  svg.append(
    arc('masquerade-vortex__ring-trail', trail, 0),
    arc('masquerade-vortex__ring-head', head, trail - head)
  );

  return svg;
}

function buildAvatar(avatar: VortexAvatar): HTMLElement {
  const node = div('masquerade-vortex__avatar');
  if (avatar.background) node.style.setProperty('--masquerade-avatar-bg', avatar.background);

  // Mode fun : pas de portrait à attendre, l'icône est déjà là.
  if (avatar.glyph) {
    node.classList.add('masquerade-vortex__avatar--glyph');
    const glyph = document.createElement('i');
    glyph.className = avatar.glyph;
    node.appendChild(glyph);
    return node;
  }

  const url = safeAvatarUrl(avatar.url);
  const showInitials = () => {
    node.replaceChildren();
    node.classList.remove(
      'masquerade-vortex__avatar--pending',
      'masquerade-vortex__avatar--wordmark',
      'masquerade-vortex__avatar--contain'
    );
    node.classList.add('masquerade-vortex__avatar--initials');
    node.textContent = avatar.initials;
  };

  // Pas d'image attendue : les initiales sont le seul contenu possible.
  if (!url) {
    showInitials();
    return node;
  }

  // Une image est attendue : on ne montre RIEN en attendant. La boîte finale est réservée
  // pour que rien ne bouge quand le logo arrive, mais sans fond ni bordure — afficher des
  // initiales en attendant faisait changer la pastille de forme en pleine animation, et ce
  // changement se lisait comme un raté plutôt que comme un effet.
  node.classList.add('masquerade-vortex__avatar--pending');
  if (avatar.shape === 'wordmark') node.classList.add('masquerade-vortex__avatar--wordmark');
  if (avatar.fit === 'contain') node.classList.add('masquerade-vortex__avatar--contain');

  const image = new Image();
  image.alt = '';
  if (avatar.theme) {
    // Même traitement que le sélecteur d'espace : le contrôleur `brand-logo` analyse l'image
    // et décide d'inverser un logo sombre découpé en thème nuit, ou de lui poser une plaque.
    // Il sait attendre le chargement, on peut donc le brancher tout de suite.
    image.className = avatar.theme === 'invert' ? 'brand-logo' : 'brand-logo-solid';
    if (avatar.theme === 'solid') image.dataset.controller = 'brand-logo';
  }
  image.addEventListener('load', () => node.classList.remove('masquerade-vortex__avatar--pending'), {
    once: true,
  });
  // Dernier recours : une image illisible (une société a un « logo » en PDF) laisserait
  // sinon un trou au centre du vortex.
  image.addEventListener('error', showInitials, { once: true });
  node.appendChild(image);
  image.src = url;

  return node;
}

// La pastille rendue par le serveur porte `--pending` : c'est au chargement de l'image qu'on
// la révèle. Ici l'image peut déjà être arrivée pendant le téléchargement du module — d'où
// le test de `complete` avant de poser un écouteur.
function adoptServerAvatar(overlay: HTMLElement): void {
  const node = overlay.querySelector<HTMLElement>('.masquerade-vortex__avatar--pending');
  const image = node?.querySelector('img');
  if (!node || !image) return;

  const reveal = () => node.classList.remove('masquerade-vortex__avatar--pending');
  const giveUp = () => {
    node.replaceChildren();
    node.classList.remove(
      'masquerade-vortex__avatar--pending',
      'masquerade-vortex__avatar--wordmark',
      'masquerade-vortex__avatar--contain'
    );
    node.classList.add('masquerade-vortex__avatar--initials');
    node.textContent = node.dataset.masqueradeInitials ?? '';
  };

  if (image.complete) {
    if (image.naturalWidth > 0) reveal();
    else giveUp();
    return;
  }

  image.addEventListener('load', reveal, { once: true });
  image.addEventListener('error', giveUp, { once: true });
}

// Six fusées, comme dans la coquille rendue par le serveur (cf.
// Shared::LayoutComponents::MasqueradeVortexComponent::ROCKETS) : les deux moitiés de la
// mise en scène doivent se ressembler, sinon on voit la couture au moment où le JS prend la
// main.
const ROCKETS = 6;

function buildRockets(): HTMLElement {
  const layer = div('masquerade-vortex__rockets');
  layer.setAttribute('aria-hidden', 'true');

  for (let index = 0; index < ROCKETS; index += 1) {
    const rocket = document.createElement('i');
    rocket.className = 'fa-solid fa-rocket masquerade-vortex__rocket';
    rocket.style.setProperty('--rocket-angle', `${index * (360 / ROCKETS)}deg`);
    rocket.style.setProperty('--rocket-delay', `${index * 0.11}s`);
    layer.appendChild(rocket);
  }

  return layer;
}

function buildCore(payload: VortexPayload): HTMLElement {
  const core = div('masquerade-vortex__core');
  core.append(
    div('masquerade-vortex__shock'),
    buildRing('outer', 44, 46, 14),
    buildRing('inner', 34, 30, 10),
    div('masquerade-vortex__glow')
  );
  // Réservé au mode fun : le reste de la plateforme n'a pas à décoller.
  if (payload.direction.startsWith('arcade')) core.appendChild(buildRockets());
  core.appendChild(buildAvatar(payload.avatar));

  const identity = div('masquerade-vortex__identity');
  const name = document.createElement('span');
  name.className = 'masquerade-vortex__name';
  // textContent et pas innerHTML : nom et légende viennent de la base et de l'i18n.
  name.textContent = payload.name;
  const caption = document.createElement('span');
  caption.className = 'masquerade-vortex__caption';
  caption.textContent = payload.caption;
  identity.append(name, caption);
  core.appendChild(identity);

  return core;
}

function buildOverlay(payload: VortexPayload): HTMLElement {
  const overlay = div(`${OVERLAY_CLASS} ${OVERLAY_CLASS}--${payload.direction}`);
  // Le vortex prend la couleur de l'espace où l'on ARRIVE, pas de celui qu'on quitte : c'est
  // ce qui fait lire la bascule comme une transformation. Les deux variantes sont posées, la
  // feuille de style choisit selon le thème. Absentes (masquerade), tout retombe sur la
  // primaire courante.
  if (payload.color.light) overlay.style.setProperty('--masquerade-primary-light', payload.color.light);
  if (payload.color.dark) overlay.style.setProperty('--masquerade-primary-dark', payload.color.dark);
  overlay.setAttribute('role', 'status');
  overlay.setAttribute('aria-live', 'polite');
  overlay.setAttribute('aria-label', payload.caption || payload.name);
  return overlay;
}

function removeOverlays(): void {
  document.querySelectorAll(`.${OVERLAY_CLASS}`).forEach((node) => node.remove());
}

// ---------------------------------------------------------------------------------------
// Géométrie du vortex : les morceaux ne filent pas en ligne droite vers le centre, ils
// l'atteignent par une spirale. Le point milieu est décalé sur la perpendiculaire au rayon,
// d'autant plus que le morceau part de loin.
// ---------------------------------------------------------------------------------------

const SWIRL = 0.28;

// Un morceau encombrant — typiquement le <canvas> d'un graphe, qu'ATOMIC_SELECTOR prend tel
// quel sans borne de taille — ne se comporte pas comme les autres. À un demi-tour près il
// balaie tout l'écran : on voyait passer une grande plaque pivotée au milieu du vortex.
//
// Il garde donc sa place dans le squelette (l'app représente bien un graphe par un gros
// bloc), mais il file DROIT au centre, sans rotation ni spirale, et se rétracte plus vite.
const isBulky = (box: ShardBox) => box.width > 320 || box.height > 220;

// Amortissement à zéro : un amortissement à 0,3 laissait encore 20 à 65° de rotation, assez
// pour qu'une plaque de 700 px traverse la page.
const spinDamping = (box: ShardBox) => (isBulky(box) ? 0 : 1);

interface Trajectory {
  midTransform: string;
  endTransform: string;
  delayRatio: number;
}

function trajectory(box: ShardBox, cx: number, cy: number, maxDistance: number, spin: number): Trajectory {
  const shardCx = box.left + box.width / 2;
  const shardCy = box.top + box.height / 2;
  const dx = cx - shardCx;
  const dy = cy - shardCy;
  const distance = Math.hypot(dx, dy) || 1;

  const swirl = isBulky(box) ? 0 : SWIRL;
  const midScale = isBulky(box) ? 0.18 : 0.34;
  const midX = dx * 0.42 - dy * swirl;
  const midY = dy * 0.42 + dx * swirl;

  return {
    midTransform: `translate(${midX}px, ${midY}px) rotate(${spin * 0.4}deg) scale(${midScale})`,
    endTransform: `translate(${dx}px, ${dy}px) rotate(${spin}deg) scale(0.04)`,
    delayRatio: distance / maxDistance,
  };
}

function maxDistanceToCenter(boxes: ShardBox[], cx: number, cy: number): number {
  return boxes.reduce((max, box) => {
    const d = Math.hypot(cx - (box.left + box.width / 2), cy - (box.top + box.height / 2));
    return Math.max(max, d);
  }, 1);
}

const spinFor = (box: ShardBox, index: number) =>
  (index % 2 === 0 ? 1 : -1) * (60 + (index % 7) * 26) * spinDamping(box);

// ---------------------------------------------------------------------------------------
// Acte I — implosion
// ---------------------------------------------------------------------------------------

function playImplosion(payload: VortexPayload): void {
  // Un clic sur une visite déjà annulée peut laisser un overlay derrière lui : deux vortex
  // superposés se lisent comme un bug.
  removeOverlays();

  const boxes = harvestOrGiveUp();
  const overlay = buildOverlay(payload);
  const cx = window.innerWidth / 2;
  const cy = window.innerHeight / 2;
  const maxDistance = maxDistanceToCenter(boxes, cx, cy);

  const fragment = document.createDocumentFragment();
  const shards = boxes.map((box, index) => {
    const shard = buildShard(box);
    fragment.appendChild(shard);
    return { shard, box, index };
  });
  overlay.appendChild(fragment);
  overlay.appendChild(buildCore(payload));
  document.body.appendChild(overlay);

  // Le fond de l'overlay monte en même temps que les morceaux apparaissent : la page ne
  // disparaît pas, elle DEVIENT son squelette.
  overlay.animate([{ opacity: 0 }, { opacity: 1 }], {
    duration: SKELETONIZE_MS,
    easing: 'ease-out',
    fill: 'backwards',
  });

  // Le miroitement n'a d'intérêt que sur un squelette immobile ; il repeint chaque morceau à
  // chaque frame, donc on le coupe dès que ça bouge.
  window.setTimeout(() => overlay.classList.add(FLYING_CLASS), SKELETONIZE_MS);

  shards.forEach(({ shard, box, index }) => {
    const { midTransform, endTransform, delayRatio } = trajectory(
      box, cx, cy, maxDistance, spinFor(box, index)
    );

    shard.animate(
      [
        { transform: 'none', opacity: 1, offset: 0 },
        { transform: midTransform, opacity: 1, offset: 0.55 },
        { transform: endTransform, opacity: 0.12, offset: 1 },
      ],
      {
        duration: IMPLODE_MS,
        // Les morceaux proches du centre partent en premier : le vortex mange la page du
        // centre vers les bords.
        delay: SKELETONIZE_MS + delayRatio * IMPLODE_STAGGER_MS,
        easing: 'cubic-bezier(.55,.06,.68,.19)',
        fill: 'forwards',
      }
    );
  });
}

// ---------------------------------------------------------------------------------------
// Acte II — explosion
// ---------------------------------------------------------------------------------------

function revealRealPage(overlay: HTMLElement): void {
  document.body.classList.remove(PENDING_CLASS);
  document.body.classList.add('masquerade-vortex-revealing');
  overlay.classList.add(`${OVERLAY_CLASS}--dissolving`);

  window.setTimeout(() => {
    overlay.remove();
    document.body.classList.remove('masquerade-vortex-revealing');
  }, REVEAL_MS);
}

function showToast(payload: VortexPayload): void {
  if (!payload.done) return;

  const toast = div('masquerade-vortex-toast');
  toast.setAttribute('role', 'status');
  const icon = document.createElement('i');
  // L'icône de l'action elle-même : masque d'espion, retour, ou double flèche de bascule.
  icon.className = payload.icon;
  icon.setAttribute('aria-hidden', 'true');
  const label = document.createElement('span');
  label.textContent = payload.done;
  toast.append(icon, label);
  document.body.appendChild(toast);

  window.setTimeout(() => {
    toast.classList.add('masquerade-vortex-toast--leaving');
    window.setTimeout(() => toast.remove(), 320);
  }, TOAST_MS);
}

function playReassembly(payload: VortexPayload): void {
  // Le serveur a normalement déjà peint la coquille (cf.
  // Shared::LayoutComponents::MasqueradeVortexComponent) : c'est elle qui tient l'écran
  // pendant que ce module se télécharge. On l'adopte telle quelle plutôt que d'en refaire
  // une, sinon le noyau sauterait au moment où le JS prend la main.
  const serverOverlay = document.querySelector<HTMLElement>(`.${OVERLAY_CLASS}`);
  const overlay = serverOverlay ?? buildOverlay(payload);
  overlay.classList.add(FLYING_CLASS);

  let core = overlay.querySelector<HTMLElement>('.masquerade-vortex__core');
  if (!core) {
    core = buildCore(payload);
    core.classList.add('masquerade-vortex__core--collapsing');
    overlay.appendChild(core);
  }
  if (!serverOverlay) document.body.appendChild(overlay);
  adoptServerAvatar(overlay);

  // Les mesures, elles, laissent passer une frame : au premier tour de boucle le document
  // vient d'être analysé, et les polices web comme les feuilles injectées par Vite peuvent
  // encore décaler la mise en page. Après un tour de rendu, les morceaux se posent là où le
  // contenu se trouvera vraiment.
  afterNextFrame(() => {
    // PENDING_CLASS met `opacity: 0` sur les enfants de <body> — or la récolte écarte
    // justement ce qui est à opacité nulle, et ne ramènerait donc RIEN. On lève le masque le
    // temps de mesurer : tout tient dans la même tâche, le navigateur ne repeint qu'à la
    // fin, donc rien n'apparaît à l'écran.
    document.body.classList.remove(PENDING_CLASS);
    const boxes = harvestOrGiveUp();
    document.body.classList.add(PENDING_CLASS);

    const cx = window.innerWidth / 2;
    const cy = window.innerHeight / 2;
    const maxDistance = maxDistanceToCenter(boxes, cx, cy);

    const fragment = document.createDocumentFragment();
    const shards = boxes.map((box, index) => {
      const shard = buildShard(box);
      fragment.appendChild(shard);
      return { shard, box, index };
    });
    // Insérés AVANT le noyau : il doit rester au-dessus des morceaux qu'il recrache.
    overlay.insertBefore(fragment, core);

    let lastLanding = BURST_DELAY_MS + BURST_MS;

    shards.forEach(({ shard, box, index }) => {
      const { midTransform, endTransform, delayRatio } = trajectory(
        box, cx, cy, maxDistance, spinFor(box, index)
      );
      const delay = BURST_DELAY_MS + delayRatio * BURST_STAGGER_MS;
      lastLanding = Math.max(lastLanding, delay + BURST_MS);

      shard.animate(
        [
          { transform: endTransform, opacity: 0.12, offset: 0 },
          { transform: midTransform, opacity: 1, offset: 0.45 },
          { transform: 'none', opacity: 1, offset: 1 },
        ],
        {
          duration: BURST_MS,
          delay,
          easing: 'cubic-bezier(.16,1,.3,1)',
          // `backwards` : avant son tour, le morceau reste tapi au centre plutôt que de
          // trôner à sa place finale.
          fill: 'backwards',
        }
      );
    });

    // Tout est posé : on rallume le miroitement. Pendant ces quelques dixièmes, l'écran est
    // exactement le squelette de chargement de la plateforme — puis le vrai contenu prend
    // sa place en fondu, comme à la fin de n'importe quel chargement.
    const revealAt = lastLanding + SHIMMER_BEFORE_REVEAL_MS;
    window.setTimeout(() => overlay.classList.remove(FLYING_CLASS), lastLanding - 120);
    window.setTimeout(() => revealRealPage(overlay), revealAt);
    window.setTimeout(() => showToast(payload), revealAt + 160);
  });
}

// ---------------------------------------------------------------------------------------
// Branchements
// ---------------------------------------------------------------------------------------

function consumeBodyPayload(): VortexPayload | null {
  const raw = document.body.dataset.masqueradeVortex;
  // Consommé une seule fois : une restauration de snapshot Turbo ne doit pas rejouer la scène.
  delete document.body.dataset.masqueradeVortex;
  return parsePayload(raw);
}

// Une scène d'arrivée ne doit se jouer qu'une fois.
//
// Le témoin posé sur <body> ne protège que dans la page courante : il part avec le corps.
// Or la charge utile peut revenir — un flash de masquerade survit à toute requête qui ne
// touche pas `flash` (les frames paresseuses du tableau de bord n'y touchent pas), et un
// snapshot Turbo restauré peut la ramener telle quelle. On retenait alors la scène pour une
// page qui l'avait déjà jouée.
//
// `sessionStorage` est le bon périmètre : par onglet, il survit aux rechargements et aux
// visites Turbo, et n'a pas à être fiable au-delà — au pire la scène se rejoue une fois.
const PLAYED_PREFIX = 'masquerade-vortex:';

function alreadyPlayed(id: string): boolean {
  if (!id) return false;

  try {
    const key = PLAYED_PREFIX + id;
    if (window.sessionStorage.getItem(key)) return true;
    window.sessionStorage.setItem(key, '1');
  } catch {
    // Navigation privée, stockage bloqué : on laisse jouer.
  }

  return false;
}

function runArrival(): void {
  if (!document.body.classList.contains(PENDING_CLASS)) return;
  // Trois déclencheurs pour couvrir chargement complet ET visite Turbo — donc trois appels
  // sur la même page. Sans ce verrou, le deuxième ne trouvait plus de charge utile, retirait
  // PENDING_CLASS et découvrait la page en plein réassemblage. Le témoin vit sur <body> :
  // Turbo le remplace, une page suivante repart donc de zéro sans qu'on ait à le remettre.
  if (document.body.dataset.masqueradeVortexPlayed === 'true') return;
  document.body.dataset.masqueradeVortexPlayed = 'true';

  const payload = consumeBodyPayload();

  if (payload && alreadyPlayed(payload.id)) {
    document.body.classList.remove(PENDING_CLASS);
    removeOverlays();
    return;
  }

  if (!payload || prefersReducedMotion()) {
    document.body.classList.remove(PENDING_CLASS);
    // La coquille rendue par le serveur est là quoi qu'il arrive : sans ce retrait, elle
    // resterait plaquée sur la page en mouvement réduit.
    removeOverlays();
    if (payload) showToast(payload);
    return;
  }

  // Filet indépendant des animations : quoi qu'il arrive, le contenu réapparaît.
  window.setTimeout(() => {
    document.body.classList.remove(PENDING_CLASS);
    removeOverlays();
  }, FAILSAFE_MS);

  playReassembly(payload);
}

// Bascule d'espace : c'est ce marqueur qui autorise le serveur à annoncer l'arrivée
// (cf. ApplicationController#flash_space_switch_vortex). Sans lui, il faudrait déduire la
// bascule du seul `current_partner_id`, et n'importe quel lien qui le porte déclencherait la
// mise en scène — y compris les liens de souscription envoyés par e-mail aux assurés, qui
// n'ont rien à faire d'un vortex. Le masquerade, lui, passe par le flash posé par son
// contrôleur : il n'a pas besoin du marqueur.
//
// Rejouer au rechargement n'est pas un risque : le serveur exige EN PLUS que le partenaire
// change réellement, et après la bascule la session porte déjà la bonne valeur.
function navigationUrl(href: string, payload: VortexPayload): string {
  if (payload.direction !== 'space') return href;

  try {
    const url = new URL(href, window.location.origin);
    url.searchParams.set('masquerade_vortex', '1');
    return url.toString();
  } catch {
    return href;
  }
}

// Bascule d'espace : VISITE TURBO, pas rechargement du navigateur.
//
// Le document n'est pas détruit : plus d'onglet qui tourne, plus de re-parse du bundle, et
// la coquille rendue par le serveur dans le nouveau corps prend le relais de l'implosion
// dans la même frame de rendu. C'est ce qui supprime la coupure entre les deux pages.
//
// Le masquerade, lui, garde un rechargement complet : l'utilisateur de session change, et
// tout l'état client (contrôleurs Stimulus, caches, connexions ouvertes) survivrait à une
// visite Turbo. Un document neuf est le choix prudent quand l'identité change.
// Vrai entre le départ d'une visite Turbo de bascule et l'arrivée du nouveau corps. C'est la
// fenêtre pendant laquelle l'overlay EST l'écran : rien ne doit le retirer.
let vortexVisitInFlight = false;

function navigate(url: string, payload: VortexPayload): void {
  const turbo = (window as any).Turbo;

  if (payload.direction === 'space' && typeof turbo?.visit === 'function') {
    vortexVisitInFlight = true;
    turbo.visit(url, { action: 'replace' });
    return;
  }

  window.location.assign(url);
}

// Turbo AJOUTE les feuilles de style du nouveau document sans retirer les anciennes : un
// `<style>` n'est pas « provisionnel » au sens de son HeadSnapshot, donc jamais remplacé.
// Or la marque de l'espace vit précisément dans un `<style>` (cf. shared/_brand_customizations) :
// sans ce nettoyage, chaque bascule en empilerait un de plus pour la durée de l'onglet.
//
// Ici et pas dans common.ts parce que c'est la visite Turbo de la bascule d'espace qui crée
// le besoin ; le listener reste global, donc il couvrira aussi d'autres liens de partenaire
// s'ils passent un jour par Turbo.
function dedupeBrandCustomizations(): void {
  const seen = new Set<string>();

  // À l'envers : Turbo ajoute à la fin, le dernier de chaque clé est donc le plus récent.
  Array.from(document.head.querySelectorAll<HTMLElement>('[data-brand-customizations]'))
    .reverse()
    .forEach((node) => {
      const key = node.dataset.brandCustomizations ?? '';
      if (seen.has(key)) node.remove();
      else seen.add(key);
    });
}

// Départ déclenché AUTREMENT que par un lien.
//
// Le mode arcade se franchit sur un compteur de clics (cinq sur une pastille) ou sur un
// bouton de menu, et la bascule est un POST : il n'y a pas de `href` à détourner, donc pas
// de place pour le branchement par `TRIGGER_SELECTOR` ci-dessous. L'appelant garde la main
// sur le départ — soumettre son formulaire, naviguer — et n'a qu'à le passer ici.
//
// Renvoie `false` quand la scène n'a pas été jouée (charge utile illisible, mouvement
// réduit) : `depart` est alors appelé immédiatement, la bascule a bien lieu, sans mise en
// scène.
export function runVortexTransition(
  rawPayload: string | null | undefined,
  depart: () => void
): boolean {
  const payload = parsePayload(rawPayload);

  if (!payload || prefersReducedMotion()) {
    depart();
    return false;
  }

  playImplosion(payload);
  // Même délai que pour un lien : le document sortant reste peint pendant toute la requête,
  // donc l'implosion couvre la latence d'un seul tenant.
  window.setTimeout(depart, DEPART_DELAY_MS);
  return true;
}

export function initializeMasqueradeVortex(): void {
  if ((window as any).__masqueradeVortexInitialized) return;
  (window as any).__masqueradeVortexInitialized = true;

  document.addEventListener('click', (event) => {
    const trigger = (event.target as Element | null)?.closest?.(TRIGGER_SELECTOR) as
      | HTMLAnchorElement
      | null;
    if (!trigger) return;
    // Clic milieu, cmd/ctrl-clic, nouvel onglet : on ne détourne rien.
    if (event.defaultPrevented || (event as MouseEvent).button !== 0) return;
    if ((event as MouseEvent).metaKey || (event as MouseEvent).ctrlKey || (event as MouseEvent).shiftKey) return;

    const payload = parsePayload(trigger.dataset.masqueradeVortex);
    if (!payload) return;

    const href = trigger.href;
    if (!href) return;

    if (prefersReducedMotion()) return;

    event.preventDefault();
    playImplosion(payload);

    // Navigation pleine page assumée : l'identité de session change, et le document sortant
    // reste peint — donc l'implosion continue de tourner pendant toute la requête, sans
    // avoir à survivre à un remplacement de <body> par Turbo.
    window.setTimeout(() => navigate(navigationUrl(href, payload), payload), DEPART_DELAY_MS);
  });

  document.addEventListener('turbo:render', dedupeBrandCustomizations);
  document.addEventListener('turbo:load', runArrival);
  document.addEventListener('DOMContentLoaded', runArrival);
  if (document.readyState !== 'loading') runArrival();

  // Un overlay ne doit jamais entrer dans un snapshot Turbo, ni la classe qui masque le corps.
  // Un overlay ne doit jamais entrer dans un snapshot Turbo, ni la classe qui masque le corps.
  //
  // SAUF pendant une bascule : là, l'overlay est ce qui tient l'écran. `turbo:before-cache`
  // se déclenche APRÈS l'arrivée de la réponse mais AVANT le rendu, et le clonage du document
  // qui suit n'est pas instantané — retirer l'overlay ici découvrait la page de départ, intacte,
  // le temps du snapshot. C'était le clignotement entre les deux animations. Le corps sortant
  // part de toute façon avec le remplacement, la coquille du nouveau corps prenant le relais
  // dans la même opération : il n'y a rien à retirer à la main.
  document.addEventListener('turbo:before-cache', () => {
    if (!vortexVisitInFlight) removeOverlays();
    document.body.classList.remove(PENDING_CLASS, 'masquerade-vortex-revealing');
    document.querySelectorAll('.masquerade-vortex-toast').forEach((node) => node.remove());
  });

  // Filet : un snapshot pris pendant une bascule peut contenir un overlay figé. Au retour
  // arrière sur cette page, il n'y a pas de scène à jouer — on nettoie.
  document.addEventListener('turbo:load', () => {
    vortexVisitInFlight = false;
    if (!document.body.classList.contains(PENDING_CLASS)) removeOverlays();
  });

  // Même filet pour le bfcache, qui rend le document tel qu'on l'a quitté : après un départ
  // pleine page (masquerade, connexion, déconnexion), c'est l'implosion qui tenait l'écran.
  // Une page restaurée ne rejoue aucune scène.
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;

    vortexVisitInFlight = false;
    removeOverlays();
    document.body.classList.remove(PENDING_CLASS, 'masquerade-vortex-revealing');
    document.querySelectorAll('.masquerade-vortex-toast').forEach((node) => node.remove());
  });
}
